import React, { useState } from 'react';

// Hooks
import { NavLink, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

// Utils
import { balanceFormatter } from '~/utils/helperFunctions';
import {
  ADMIN_NAVIGATION,
  ADMIN_PRICING_CONTROLS_NAVIGATION,
  GENERAL_NAVIGATION,
} from '~/utils/constants';
import { getImpersonationMode } from '~/utils/auth';

// Components
import NewDropDown from '~/components/Layout/Header/NewDropdown';
import SwitchBar from '~/components/AdminPortal/Users/SwitchBar';
import Stripe from '~/components/Stripe/Stripe';
import AddFunds from '~/components/Account/Wallet/AddFunds';
import PaymentMethod from '~/components/Account/Wallet/PaymentMethod';
import Mode from '~/components/Mode';
import HelpDropdown from '~/components/Layout/Header/HelpDropdown';

// Mui Components
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  Drawer,
  Divider,
  List,
  ListItem,
  Container,
  Avatar,
  useMediaQuery,
} from '@mui/material';

// Icons
import { OpenLetterLogoCIcon } from '~/assets/images';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MenuIcon from '@mui/icons-material/Menu';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

// Styles
import './styles.scss';

const drawerWidth = 280;
const ADMIN_PRIMARY_NAV_ITEM_COUNT = 5;
const ADMIN_MORE_MENU_QUERY = '(max-width:1399px)';
const ADMIN_PRICING_CONTROLS_TITLE = 'Pricing Controls';

const Header = (props) => {
  const location = useLocation();
  const { window, isAdminMode, onClick, isWidgetVisible, setIsWidgetVisible } = props;

  const [mobileOpen, setMobileOpen] = useState(false);
  // State for DropDown
  const [showDropDown, setShowDropDown] = useState(false);
  const [helpDropDown, setHelpDropDown] = useState(false);
  const [fundsModal, setFundsModal] = useState(false);
  const [paymentMehodModal, setPaymentMethodModal] = useState(false);
  const [showPricingMegaMenu, setShowPricingMegaMenu] = useState(false);
  const [showPricingDrawerMenu, setShowPricingDrawerMenu] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  // handler for dropdown
  const handleShowDropDown = () => {
    setShowDropDown((prevState) => !prevState);
  };
  const handleDropDown = () => {
    setHelpDropDown((prevState) => !prevState);
  };
  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };

  const isAdmin = useSelector((state) => state.userReducers?.user?.isOrgAdmin) || false;

  const userName = useSelector((state) => state.userReducers?.user?.fullName || '');
  const firstName = useSelector((state) => state.userReducers?.user?.firstName || '');
  const lastName = useSelector((state) => state.userReducers?.user?.lastName || '');

  const userEmail = useSelector((state) => state.userReducers?.user?.email || '');

  const organizationBalance = useSelector(
    (state) => state.userReducers?.user?.organization?.balance ?? 0
  );

  const hasPaymentMethods = useSelector(
    (state) => (state.stripeReducers?.methods?.length ?? 0) > 0
  );

  const isImpersonatedMode = getImpersonationMode() || false;

  const isSystemSuperAdmin = useSelector((state) => state.userReducers.user?.isSystemSuperAdmin);

  const currentNavigation = isAdminMode
    ? ADMIN_NAVIGATION.filter((item) => {
        // Hide Pricing Controls navigation item if user is not SystemSuperAdmin
        if (item.title === 'Pricing Controls' && !isSystemSuperAdmin) {
          return false;
        }
        return true;
      })
    : GENERAL_NAVIGATION;
  const shouldUseAdminMoreMenu = useMediaQuery(ADMIN_MORE_MENU_QUERY);

  const isNavItemActive = (item) => {
    const childRoutes = item?.childrens || [];

    if (item?.title === 'Pricing Controls') {
      return location.pathname.startsWith('/admin/pricing-controls');
    }

    return location.pathname === item?.link || childRoutes.includes(location.pathname);
  };

  const adminPrimaryNavigation =
    isAdminMode && shouldUseAdminMoreMenu
      ? [
          ...currentNavigation.slice(0, ADMIN_PRIMARY_NAV_ITEM_COUNT),
          ...currentNavigation
            .slice(ADMIN_PRIMARY_NAV_ITEM_COUNT)
            .filter((item) => item.title === ADMIN_PRICING_CONTROLS_TITLE),
        ]
      : currentNavigation;
  const adminMoreNavigation =
    isAdminMode && shouldUseAdminMoreMenu
      ? currentNavigation
          .slice(ADMIN_PRIMARY_NAV_ITEM_COUNT)
          .filter((item) => item.title !== ADMIN_PRICING_CONTROLS_TITLE)
      : [];

  const isMoreActive = adminMoreNavigation.some((item) => isNavItemActive(item));

  const renderPricingMenu = () => (
    <Box className="pricingMegaMenu">
      <Box className="pricingMegaMenuList">
        {ADMIN_PRICING_CONTROLS_NAVIGATION.map((item) => (
          <NavLink key={item.id} className="pricingMegaMenuLink" to={item.link}>
            <ListItem>{item.title}</ListItem>
          </NavLink>
        ))}
      </Box>
    </Box>
  );

  const renderDesktopNavItem = (item) => {
    const isPricingControls = item?.title === 'Pricing Controls';
    const isPricingActive = isNavItemActive(item);

    return (
      <Box
        key={`${item?.id}-${item?.title}`}
        className="desktopNavItem"
        onMouseEnter={() => {
          if (isPricingControls) setShowPricingMegaMenu(true);
        }}
        onMouseLeave={() => {
          if (isPricingControls) setShowPricingMegaMenu(false);
        }}
      >
        {isPricingControls ? (
          <Box className={isPricingActive ? 'activeItem navItemWithoutLink' : 'navItemWithoutLink'}>
            <ListItem>{item?.title}</ListItem>
          </Box>
        ) : (
          <NavLink to={item?.link} className={() => (isNavItemActive(item) ? 'activeItem' : '')}>
            <ListItem>{item?.title}</ListItem>
          </NavLink>
        )}
        {isPricingControls && showPricingMegaMenu && isSystemSuperAdmin && renderPricingMenu()}
      </Box>
    );
  };

  const handlePaymentModal = () => {
    hasPaymentMethods ? setFundsModal(true) : setPaymentMethodModal(true);
  };

  const isNavigationItemActive = (item) => {
    if (!item) return false;
    if (location.pathname === item.link) return true;

    return item.childrens?.some(
      (path) => location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
  };
  const dashboardPath = isAdminMode ? '/admin/overview' : '/overview';
  const userInitials = `${firstName?.[0] || ''}${lastName?.[0] || ''}`;

  const AvatarStyle = {
    width: 38,
    height: 38,
    backgroundColor: showDropDown ? '#DD5026' : '#ED5C2F',
    borderRadius: '50%',
    cursor: 'pointer',
    color: '#fff',
    fontSize: '13px',
    fontWeight: 600,
    transition: 'background-color 0.3s ease',
    '&:hover': {
      backgroundColor: '#DD5026',
    },
  };

  const drawer = (
    <Box className="mobileDrawer">
      <NavLink className="mobileDrawerLogoLink" onClick={handleDrawerToggle} to={dashboardPath}>
        <img src={OpenLetterLogoCIcon} alt="open-letter" />
      </NavLink>
      <Divider />
      <List
        className="mobileDrawerList"
        onClick={(event) => {
          if (event.target.closest('a')) handleDrawerToggle();
        }}
      >
        {currentNavigation.map((item) => {
          const isPricingControls = item?.title === 'Pricing Controls';
          const isPricingActive =
            isPricingControls && location.pathname.startsWith('/admin/pricing-controls');

          if (isPricingControls && isAdminMode && isSystemSuperAdmin) {
            return (
              <Box
                key={item?.id}
                onMouseEnter={() => setShowPricingDrawerMenu(true)}
                onMouseLeave={() => setShowPricingDrawerMenu(false)}
                sx={{ position: 'relative' }}
              >
                <Box
                  component="button"
                  className={
                    isPricingActive ? 'activeItem pricingControlsHeader' : 'pricingControlsHeader'
                  }
                  onClick={() => setShowPricingDrawerMenu((isOpen) => !isOpen)}
                  sx={{
                    background: 'transparent',
                    border: 0,
                    cursor: 'pointer',
                    padding: 0,
                    textAlign: 'left',
                    width: '100%',
                  }}
                  type="button"
                >
                  <ListItem>{item?.title}</ListItem>
                </Box>
                {showPricingDrawerMenu && (
                  <Box className="pricingControlsSubMenu">
                    {ADMIN_PRICING_CONTROLS_NAVIGATION.map((pricingItem) => (
                      <NavLink
                        key={pricingItem.id}
                        to={pricingItem.link}
                        className={({ isActive }) =>
                          isActive || location.pathname === pricingItem.link ? 'activeItem' : ''
                        }
                      >
                        <ListItem>{pricingItem.title}</ListItem>
                      </NavLink>
                    ))}
                  </Box>
                )}
              </Box>
            );
          }

          return (
            <NavLink
              key={item?.id}
              to={item?.link}
              className={({ isActive }) =>
                isActive || item.childrens.includes(location.pathname) ? 'activeItem' : ''
              }
            >
              <ListItem>{item?.title}</ListItem>
            </NavLink>
          );
        })}
      </List>
      {!isAdminMode && (
        <Button onClick={isAdmin ? handlePaymentModal : undefined} className="balanceBtnNav">
          {balanceFormatter(organizationBalance)}
        </Button>
      )}
    </Box>
  );
  const container = window !== undefined ? () => window().document.body : undefined;
  const appBarClassNames = ['headerAppBar'];

  appBarClassNames.push(isAdminMode ? 'adminHeaderAppBar' : 'userHeaderAppBar');

  if (isImpersonatedMode) {
    appBarClassNames.push('headerTop');
  }

  return (
    <>
      <AppBar component="nav" className={appBarClassNames.join(' ')}>
        <Container maxWidth="xl">
          <Toolbar className="headerToolBar">
            <Box className={`userHeaderNav${isAdminMode ? ' adminHeaderNav' : ''}`}>
              {isImpersonatedMode && <SwitchBar />}
              <Box className="userHeaderBrandCluster">
                <button
                  aria-label="open drawer"
                  className="hamburgerIcon userHeaderMenuButton"
                  onClick={handleDrawerToggle}
                  type="button"
                >
                  <MenuIcon sx={{ color: '#1f2328', fontSize: 24 }} />
                </button>
                <NavLink className="userHeaderLogoLink" to={dashboardPath}>
                  <img className="userHeaderLogo" src={OpenLetterLogoCIcon} alt="open-letter" />
                </NavLink>
              </Box>
              <Box className="userHeaderDivider" />
              <Box className="userHeaderLinks">
                {adminPrimaryNavigation.map((item) => {
                  const isPricingControls = item.title === 'Pricing Controls';
                  const isPricingActive =
                    isPricingControls && location.pathname.startsWith('/admin/pricing-controls');

                  if (isPricingControls) {
                    return (
                      <Box
                        className="adminPricingNavItem"
                        key={item.id}
                        onBlur={(event) => {
                          if (!event.currentTarget.contains(event.relatedTarget)) {
                            setShowPricingMegaMenu(false);
                          }
                        }}
                        onFocus={() => setShowPricingMegaMenu(true)}
                        onMouseEnter={() => setShowPricingMegaMenu(true)}
                        onMouseLeave={() => setShowPricingMegaMenu(false)}
                      >
                        <button
                          aria-expanded={showPricingMegaMenu}
                          aria-haspopup="menu"
                          className={`userHeaderLink userHeaderLinkButton${
                            isPricingActive ? ' active' : ''
                          }`}
                          type="button"
                        >
                          {item.title}
                        </button>
                        {showPricingMegaMenu && (
                          <Box className="pricingMegaMenu" role="menu">
                            <NavLink
                              onClick={() => setShowPricingMegaMenu(false)}
                              to="/admin/pricing-controls/postage"
                              role="menuitem"
                            >
                              Postage
                            </NavLink>
                            <NavLink
                              onClick={() => setShowPricingMegaMenu(false)}
                              to="/admin/pricing-controls/cost-of-good"
                              role="menuitem"
                            >
                              Cost of Goods
                            </NavLink>
                            <NavLink
                              onClick={() => setShowPricingMegaMenu(false)}
                              to="/admin/pricing-controls/pricing"
                              role="menuitem"
                            >
                              Pricing
                            </NavLink>
                            <NavLink
                              onClick={() => setShowPricingMegaMenu(false)}
                              to="/admin/pricing-controls/plans"
                              role="menuitem"
                            >
                              Plans
                            </NavLink>
                          </Box>
                        )}
                      </Box>
                    );
                  }

                  return (
                    <NavLink
                      key={item.id}
                      to={item.link}
                      className={({ isActive }) =>
                        isActive || isNavigationItemActive(item)
                          ? 'userHeaderLink active'
                          : 'userHeaderLink'
                      }
                    >
                      {item.title}
                    </NavLink>
                  );
                })}
                {isAdminMode && adminMoreNavigation.length > 0 && (
                  <Box
                    className="navMoreWrapper"
                    onBlur={(event) => {
                      if (!event.currentTarget.contains(event.relatedTarget)) {
                        setShowMoreMenu(false);
                      }
                    }}
                    onMouseEnter={() => setShowMoreMenu(true)}
                    onMouseLeave={() => {
                      setShowMoreMenu(false);
                      setShowPricingMegaMenu(false);
                    }}
                  >
                    <Button
                      aria-expanded={showMoreMenu ? 'true' : 'false'}
                      aria-haspopup="menu"
                      className={isMoreActive ? 'navMoreButton active' : 'navMoreButton'}
                      endIcon={<KeyboardArrowDownIcon />}
                      onClick={() => setShowMoreMenu((isOpen) => !isOpen)}
                    >
                      More
                    </Button>
                    {showMoreMenu && (
                      <Box className="navMoreMenu" role="menu">
                        {adminMoreNavigation.map((item) => renderDesktopNavItem(item))}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
              <Box className="userHeaderActions">
                {!isAdminMode && (
                  <>
                    <Box className="userHeaderMode">
                      <Mode />
                    </Box>
                    <Button
                      onClick={isAdmin ? handlePaymentModal : undefined}
                      className="userHeaderWallet"
                      startIcon={
                        <AccountBalanceWalletOutlinedIcon className="userHeaderWalletIcon" />
                      }
                    >
                      {balanceFormatter(organizationBalance, 2, true)}
                    </Button>
                  </>
                )}
                <Box className="helpCenter avatarWrapper userHeaderIconWrapper">
                  <button
                    aria-label="Open help menu"
                    className="userHeaderIconButton"
                    onClick={handleDropDown}
                    type="button"
                  >
                    <HelpOutlineIcon aria-hidden="true" />
                  </button>
                  {helpDropDown ? (
                    <HelpDropdown
                      setHelpDropDown={setHelpDropDown}
                      dropValue={helpDropDown}
                      isAdminMode={isAdminMode}
                      onClick={onClick}
                      isWidgetVisible={isWidgetVisible}
                      setIsWidgetVisible={setIsWidgetVisible}
                    />
                  ) : (
                    ''
                  )}
                </Box>
                <Box className="avatarWrapper userHeaderAvatarWrapper">
                  <Avatar sx={AvatarStyle} onClick={handleShowDropDown}>
                    {userInitials || <AccountCircleOutlinedIcon aria-hidden="true" />}
                  </Avatar>
                  {showDropDown ? (
                    <NewDropDown
                      setShowDropDown={setShowDropDown}
                      isAdminMode={isAdminMode}
                      dropValue={showDropDown}
                      userName={userName}
                      userEmail={userEmail}
                      firstName={firstName}
                      lastName={lastName}
                    />
                  ) : (
                    ''
                  )}
                </Box>
              </Box>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
      <nav>
        <Drawer
          container={container}
          variant="temporary"
          className="drawerMobileWrapper"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'block', lg: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
      </nav>
      {fundsModal && (
        <AddFunds
          open={fundsModal}
          handleClose={() => {
            setFundsModal(false);
          }}
        />
      )}
      {paymentMehodModal && (
        <Stripe>
          <PaymentMethod
            open={paymentMehodModal}
            handleClose={() => {
              setPaymentMethodModal(false);
            }}
          />
        </Stripe>
      )}
    </>
  );
};

export default Header;
